import { forEach, map, concat, flatMap, flatten, partition } from 'lodash-es'
import { get, call } from 'vuex-pathify'
import moment from 'moment'

export default {
  async mounted () {
    if (this.authenticatedUser && this.authenticatedUser.stats.bookingsConfirmed) {
      await this.loadConfirmedBookings()
    }
  },
  methods: {
    ...call({
      loadConfirmedBookings: 'myKintell/loadBookingsConfirmed',
    }),
    getBusyTimes (busyTimes) {
      const advisorsUnavailable = []
      forEach(busyTimes, unavailable => {
        // Create moment UTC timestamp from string
        const timestamp = moment.utc(unavailable.timestampUtc)

        advisorsUnavailable.push(
          {
            duration: unavailable.duration,
            timestampTz: timestamp.clone().tz(this.timezone),
            timestampUtc: timestamp,
            isBooking: true
          }
        )
      })

      // Some days have durations that go over to the next day
      // Split unavailable times into times that cover a single day and multiple days
      const splitSingleDaysAndMultipleDays = partition(advisorsUnavailable, busyTime => {
        const timezoneTimeInMinutes = this.convertTimeToMinutes(busyTime.timestampTz.format('HH:mm'))
        return timezoneTimeInMinutes + busyTime.duration <= 1440
      })

      const convertSingleDayToMultipleDays = map(splitSingleDaysAndMultipleDays[1], busyTime => {
        const timezoneTimeInMinutes = this.convertTimeToMinutes(busyTime.timestampTz.format('HH:mm'))
        const timeToMidnight = 1440 - timezoneTimeInMinutes
        const durationToAddToNextDay = busyTime.duration - timeToMidnight
        return [
          {
            duration: timeToMidnight,
            timestampTz: busyTime.timestampTz,
            timestampUtc: busyTime.timestampUtc,
            isBooking: true
          }, {
            duration: durationToAddToNextDay,
            timestampTz: busyTime.timestampTz.clone().add('m', timeToMidnight),
            timestampUtc: busyTime.timestampUtc,
            isBooking: true
          }
        ]
      })

      const flattenSingleDays = flatten(convertSingleDayToMultipleDays, 1)

      return [
        ...flattenSingleDays,
        ...splitSingleDaysAndMultipleDays[0]
      ]
    },
    getUnavailabilityCalendarTimes (unavailabilityTimes) {
      const unavailabilityCalendarTimes = []
      // for each unavailablility time, create two objects because the interval would be for two weeks
      forEach(unavailabilityTimes, unavailable => {
        const timestampTz = moment
          .tz(this.timezone) // convert to user's timezone
          .hour(this.getHour(unavailable.time))
          .minute(this.getMinute(unavailable.time))
          .second(0)

        // If unavailable day is today using the day method can change the date to the next week
        if (timestampTz.isoWeekday() !== unavailable.day) {
          timestampTz.day(parseInt(unavailable.day))
        }

        forEach([ 0, 7, 14, 21, 28 ], day => {
          const timestampClone = timestampTz
            .clone()
            .add(day, 'd')

          unavailabilityCalendarTimes.push({
            duration: unavailable.duration,
            timestampTz: timestampClone,
            timestampUtc: timestampClone.clone().utc(),
          })
        })
      })
      return unavailabilityCalendarTimes
    },
    getFormattedBusyTimes (intervals) {
      return map(
        intervals,
        timeSlot => {
          // Convert Times when the user is book to moment timestamp
          if (timeSlot.bookingTimeUtc || typeof timeSlot.timestampUtc === 'string') {
            const timestamp = timeSlot.bookingTimeUtc ? timeSlot.bookingTimeUtc : timeSlot.timestampUtc

            timeSlot.timestampUtc = moment.utc(timestamp)
            timeSlot.timestampTz = moment.utc(timestamp).tz(this.timezone)
          }

          return {
            timestampTz: timeSlot.timestampTz,
            timestampUtc: timeSlot.timestampUtc,
            duration: timeSlot.duration
          }
        }
      )
    },
  },
  computed: {
    ...get({
      bookingsConfirmed: 'myKintell/bookingsConfirmed',
      bookingsRequested: 'myKintell/bookingRequestsLearning'
    }),
    confirmedBookingsIntervals () {
      return map(this.bookingsConfirmed, booking => {
        return {
          duration: booking.duration,
          bookingTimeUtc: booking.bookingStartTimeUtc
        }
      })
    },
    timesUserHasRequestedABooking () {
      return flatMap(this.bookingsRequested, booking => booking.bookingTimesSuggested)
    },
    timesLearnerCantBook () {
      return [
        ...this.timesWhenAdvisorOrAuthenticatedUserIsBooked,
        ...this.getFormattedBusyTimes(this.timesUserHasRequestedABooking)
      ]
    },
    timesWhenAdvisorOrAuthenticatedUserIsBooked () {
      let times = [
        ...this.getFormattedBusyTimes(concat(
          // exclude times for our confirmed bookings
          this.confirmedBookingsIntervals,
          // exclude the times of the next weeks that we have blocked off in our calendar
          this.authenticatedUser ? this.authenticatedUser.calendarBusyTimes : []
        ))
      ]
      // only when the calendar is set
      if (this.otherUserCalendar) {
        // get the other user's `busy times` which are the times they have bookings
        const busyTimes = this.getBusyTimes(this.otherUserCalendar.busyTimes)
        // get the other user's availability calendar
        const unavailableCalendarTimes = this.getUnavailabilityCalendarTimes(this.otherUserCalendar.unavailabilityTimes)
        times = [
          ...times,
          ...busyTimes,
          ...unavailableCalendarTimes,
        ]
      }
      return times
    },
  }
}
